/**
 * BootScreen - the single first-boot loading screen (replaces the old
 * stacked #loading-screen + first-load #level-transition-screen overlays).
 *
 * Shows a real weighted progress bar (fed by oggi:boot-progress from
 * src/utils/bootProgress.js), rotating "did you know" tips, and - for
 * first-run users - the comfort question inline, so the wait time answers
 * "how do you want to explore?" and the world appears with no modal in the
 * way. The screen holds until BOTH the scene is ready and the question is
 * answered (returning users auto-continue).
 *
 * In-world level SWITCHES still use the lightweight #level-transition-screen
 * (src/ui/levelTransition.js) - this module is first boot only.
 */
import { BRAND, createIcon } from './designSystem.js';
import { Lightbulb } from 'lucide';
import { buildComfortCards } from './onboarding/onboardingFlow.js';
import { isTouchDevice } from '../input/controlScheme.js';

const TIPS = [
  'Press Esc anytime to open settings',
  'You can switch control styles later in Settings > Controls',
  'Click the screens and displays to explore videos and brochures',
  'A prize wheel is waiting for you - give it a spin',
  'Chat with others from the meeting toolbar along the bottom',
  'Look for the drone kiosk to start a drone show',
];

const TIP_ROTATE_MS = 4500;

let active = false;

/** @returns {boolean} whether the boot screen is currently covering the app */
export function bootScreenActive() {
  return active;
}

/**
 * Show the boot screen. Call once, right when the user leaves the arrival
 * screen.
 * @param {{needsOnboarding: boolean}} opts - first-run users get the comfort
 *   question inline; returning users just watch the bar.
 * @returns {{
 *   choicePromise: Promise<string|null>,
 *   showError: (msg?: string) => void,
 *   hide: () => Promise<void>,
 * }}
 */
export function showBootScreen({ needsOnboarding }) {
  active = true;

  const overlay = document.createElement('div');
  overlay.id = 'boot-screen';
  overlay.style.cssText = `
    position:fixed; inset:0; z-index:1500;
    display:flex; align-items:center; justify-content:center;
    background:${BRAND.heroGradient}; font-family:${BRAND.font}; color:#111827;
    padding:24px; box-sizing:border-box; overflow-y:auto;
    transition:opacity 0.3s ease;
  `;

  const inner = document.createElement('div');
  inner.style.cssText =
    'width:min(720px, 100%); display:flex; flex-direction:column; gap:1.4rem; align-items:center; text-align:center;';
  overlay.appendChild(inner);

  // --- headline ------------------------------------------------------------
  const headline = document.createElement('h2');
  headline.id = 'boot-headline';
  headline.style.cssText = 'margin:0; font-size:1.5rem; font-weight:700; letter-spacing:-0.02em;';
  headline.textContent = needsOnboarding
    ? 'How would you like to explore?'
    : 'Preparing your experience';
  inner.appendChild(headline);

  // --- comfort question (first run only) -----------------------------------
  let resolveChoice;
  /** @type {Promise<string|null>} */
  const choicePromise = new Promise((resolve) => {
    resolveChoice = resolve;
  });
  let answered = !needsOnboarding;
  if (needsOnboarding) {
    const sub = document.createElement('p');
    sub.style.cssText = 'margin:-0.9rem 0 0; font-size:0.9rem; color:#6b7280;';
    sub.textContent = 'Pick one while the world loads - you can change it later in Settings.';
    inner.appendChild(sub);
    inner.appendChild(
      buildComfortCards(isTouchDevice(), (choice) => {
        answered = true;
        updateStatus();
        resolveChoice(choice);
      }),
    );
  } else {
    resolveChoice(null);
  }

  // --- progress bar --------------------------------------------------------
  const barWrap = document.createElement('div');
  barWrap.style.cssText =
    'width:min(420px, 100%); display:flex; flex-direction:column; gap:0.5rem; align-items:center;';
  const track = document.createElement('div');
  track.id = 'boot-progress';
  track.setAttribute('role', 'progressbar');
  track.style.cssText =
    'width:100%; height:8px; border-radius:999px; background:#e5e7eb; overflow:hidden;';
  const fill = document.createElement('div');
  fill.id = 'boot-progress-fill';
  fill.style.cssText = `height:100%; width:0%; border-radius:999px; background:${BRAND.gradient}; transition:none;`;
  track.appendChild(fill);
  const status = document.createElement('div');
  status.id = 'boot-status';
  status.style.cssText = 'font-size:0.82rem; color:#6b7280;';
  barWrap.append(track, status);
  inner.appendChild(barWrap);

  // --- rotating tips -------------------------------------------------------
  const tip = document.createElement('div');
  tip.id = 'boot-tip';
  tip.style.cssText =
    'display:flex; align-items:center; gap:0.5rem; font-size:0.85rem; color:#6b7280; min-height:1.4em; transition:opacity 0.4s ease;';
  const tipIcon = createIcon(Lightbulb, { size: 15 });
  const tipText = document.createElement('span');
  tip.append(tipIcon, tipText);
  inner.appendChild(tip);

  let tipIndex = Math.floor(Math.random() * TIPS.length);
  tipText.textContent = TIPS[tipIndex];
  const tipTimer = setInterval(() => {
    tip.style.opacity = '0';
    setTimeout(() => {
      tipIndex = (tipIndex + 1) % TIPS.length;
      tipText.textContent = TIPS[tipIndex];
      tip.style.opacity = '1';
    }, 400);
  }, TIP_ROTATE_MS);

  document.body.appendChild(overlay);

  // --- progress model ------------------------------------------------------
  // Real progress arrives as oggi:boot-progress; the displayed width eases
  // toward it every frame and caps at 97% until the scene is ready AND the
  // question is answered, so the bar never sits full while the screen holds.
  let target = 0;
  let displayed = 0;
  let sceneDone = false;
  let rafId = null;

  function updateStatus() {
    if (!sceneDone) {
      status.textContent = answered ? 'Almost there…' : 'Loading your experience…';
    } else {
      status.textContent = answered ? 'Ready!' : 'Ready - pick how you want to explore';
    }
  }
  updateStatus();

  const onProgress = (e) => {
    const detail = /** @type {CustomEvent} */ (e).detail || {};
    if (typeof detail.overall === 'number') {
      target = Math.max(target, detail.overall);
      if (detail.overall >= 1) {
        sceneDone = true;
        updateStatus();
      }
    }
  };
  window.addEventListener('oggi:boot-progress', onProgress);

  function frame() {
    const cap = sceneDone && answered ? 1 : 0.97;
    const goal = Math.min(target === 0 ? 0.04 : target, cap); // tiny head start so the bar moves immediately
    displayed += (goal - displayed) * 0.06;
    if (goal === 1 && goal - displayed < 0.005) displayed = 1;
    fill.style.width = `${(displayed * 100).toFixed(1)}%`;
    rafId = requestAnimationFrame(frame);
  }
  rafId = requestAnimationFrame(frame);

  function stopTimers() {
    clearInterval(tipTimer);
    if (rafId) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }
    window.removeEventListener('oggi:boot-progress', onProgress);
  }

  /**
   * Boot failed (window.SCENE_ERROR): swap the content for an error state
   * with a reload button. The screen stays up - there is nothing behind it.
   * @param {string} [msg]
   */
  function showError(msg) {
    stopTimers();
    inner.replaceChildren();
    const title = document.createElement('h2');
    title.style.cssText = 'margin:0; font-size:1.4rem; font-weight:700; color:#dc2626;';
    title.textContent = 'Something went wrong';
    const detail = document.createElement('p');
    detail.style.cssText = 'margin:0; font-size:0.9rem; color:#6b7280; max-width:420px;';
    detail.textContent = msg || 'The experience failed to load. Please reload to try again.';
    const btn = document.createElement('button');
    btn.textContent = 'Reload';
    btn.style.cssText = `
      padding:0.8rem 2.2rem; border:none; border-radius:9999px; cursor:pointer;
      font-family:inherit; font-size:1rem; font-weight:600; color:#fff;
      background:${BRAND.gradient}; box-shadow:${BRAND.shadow};
    `;
    btn.addEventListener('click', () => window.location.reload());
    inner.append(title, detail, btn);
  }

  /** Fade out and remove. Resolves when the overlay is gone. */
  function hide() {
    stopTimers();
    fill.style.width = '100%';
    return new Promise((resolve) => {
      overlay.style.opacity = '0';
      setTimeout(() => {
        overlay.remove();
        active = false;
        resolve(undefined);
      }, 300);
    });
  }

  return { choicePromise, showError, hide };
}
